/**
 * BL-elevar-add-to-cart-bridge.js
 *
 * Optional custom bridge for consolidating multiple `dl_add_to_cart` events that
 * Elevar emits when a single `/cart/add.js` call carries an `items[]` array
 * (bundle PDP).
 *
 * Compliance note, Elevar Support 2026-05-06 / Ticket 45147083580:
 * Elevar's default and supported bundle behavior is one AddToCart event per
 * individual bundle product / line item. This bridge is therefore DISABLED by
 * default and only runs when explicitly enabled for QA/custom testing.
 *
 * Design notes (validated by tracking expert):
 * - DOES NOT generate a manual event_id. Elevar/GTM downstream is responsible
 *   for event_id generation when forwarding ElevarDataLayer → dataLayer. We
 *   preserve whatever the template event has.
 * - When enabled, polls until window.ElevarDataLayer exists, then wraps push.
 * - When enabled, optional spurious-remove cleanup even when buffer has exactly
 *   1 event (in case Elevar reconciliation includes a `remove` for a product
 *   also in `add`).
 * - Activatable by URL flag during QA/custom testing. Default behavior:
 *     - Disabled by default to preserve Elevar's compliant native behavior.
 *     - `?bl_atc_bridge=on` enables custom consolidation for A/B comparison.
 *     - `?bl_debug=cart` enables verbose console logging.
 *
 * Custom-mode buffer window: 100ms — empirical evidence (Step Zero QA) shows
 * the N events arrive within ~0.2ms of each other; 100ms is a safe overkill that
 * absorbs variability without delaying the user-perceived flow noticeably.
 */
(function () {
  'use strict';

  if (window.__blElevarAddToCartBridgeInstalled) return;
  window.__blElevarAddToCartBridgeInstalled = true;

  const params = new URLSearchParams(window.location.search);
  const VERBOSE = (params.get('bl_debug') || '').includes('cart');
  const bridgeMode = (params.get('bl_atc_bridge') || '').toLowerCase();
  const ENABLED = ['on', '1', 'true', 'custom', 'consolidate'].includes(bridgeMode);

  if (!ENABLED) {
    if (VERBOSE) {
      // eslint-disable-next-line no-console
      console.log(
        '%c[BL-atc-bridge] PASSIVE — Elevar native AddToCart preserved. Use ?bl_atc_bridge=on for custom consolidation.',
        'color:#fff;background:#2563eb;padding:2px 6px;border-radius:3px'
      );
    }
    return;
  }

  const BUFFER_WINDOW_MS = 100;
  const ATTACH_RETRY_MS = 80;
  const ATTACH_MAX_TRIES = 60; // ~5s of patience

  const buffer = [];
  let flushTimer = null;
  let originalPush = null;

  function logVerbose(label, payload) {
    if (!VERBOSE) return;
    // eslint-disable-next-line no-console
    console.log(
      `%c[BL-atc-bridge] ${label}`,
      'color:#7c3aed;font-weight:bold',
      payload || ''
    );
  }

  function clone(value) {
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (e) {
      return value;
    }
  }

  function isAddToCart(item) {
    return item && typeof item === 'object' && item.event === 'dl_add_to_cart';
  }

  function variantKey(product) {
    return String(product?.variant_id || product?.product_id || product?.id || '');
  }

  /**
   * Removes products from `ecommerce.remove.products[]` that also appear in
   * `ecommerce.add.products[]`. Those entries are the spurious "remove"
   * artifacts seen in original datalayers (same product as `add` and `remove`),
   * caused by Elevar reconciling intermediate cart snapshots.
   */
  function cleanSpuriousRemove(event) {
    const ecom = event && event.ecommerce;
    const addList = ecom && ecom.add && Array.isArray(ecom.add.products) ? ecom.add.products : null;
    const removeList = ecom && ecom.remove && Array.isArray(ecom.remove.products) ? ecom.remove.products : null;

    if (!addList || !removeList || addList.length === 0 || removeList.length === 0) {
      return event;
    }

    const addKeys = new Set(addList.map(variantKey));
    const cleaned = removeList.filter((p) => !addKeys.has(variantKey(p)));

    if (cleaned.length === removeList.length) {
      return event; // nothing to clean
    }

    const out = clone(event);
    if (cleaned.length === 0) {
      delete out.ecommerce.remove;
    } else {
      out.ecommerce.remove.products = cleaned;
    }
    out._blRemoveCleaned = true;
    return out;
  }

  /**
   * Builds a single `dl_add_to_cart` from N buffered events.
   * - Uses the first event as the template (preserves user_properties, marketing,
   *   page, device, _elevar_internal, etc.).
   * - DOES NOT touch event_id — Elevar/GTM handles it downstream.
   * - Collapses products by variant_id, summing quantity if duplicated.
   * - Filters spurious removes that match any added product.
   */
  function consolidate(events) {
    if (events.length === 1) {
      return cleanSpuriousRemove(events[0]);
    }

    const out = clone(events[0]);
    out.ecommerce = out.ecommerce || {};
    out.ecommerce.add = out.ecommerce.add || {};

    const productsByKey = new Map();

    events.forEach((evt) => {
      const products = evt && evt.ecommerce && evt.ecommerce.add && Array.isArray(evt.ecommerce.add.products)
        ? evt.ecommerce.add.products
        : [];
      products.forEach((product) => {
        const key = variantKey(product);
        if (!key) return;
        if (productsByKey.has(key)) {
          const existing = productsByKey.get(key);
          const existingQty = parseInt(existing.quantity, 10) || 1;
          const incomingQty = parseInt(product.quantity, 10) || 1;
          existing.quantity = String(existingQty + incomingQty);
        } else {
          productsByKey.set(key, clone(product));
        }
      });
    });

    out.ecommerce.add.products = Array.from(productsByKey.values());

    // Filter spurious remove entries
    if (out.ecommerce.remove && Array.isArray(out.ecommerce.remove.products)) {
      const addKeys = new Set(out.ecommerce.add.products.map(variantKey));
      const cleaned = out.ecommerce.remove.products.filter((p) => !addKeys.has(variantKey(p)));
      if (cleaned.length === 0) {
        delete out.ecommerce.remove;
      } else {
        out.ecommerce.remove.products = cleaned;
      }
    }

    out._blConsolidated = true;
    out._blConsolidatedCount = events.length;

    return out;
  }

  function flush() {
    flushTimer = null;
    if (buffer.length === 0 || !originalPush) return;

    const events = buffer.splice(0, buffer.length);
    const result = consolidate(events);

    logVerbose(`flush — ${events.length} input → 1 output`, {
      input_count: events.length,
      product_count: result?.ecommerce?.add?.products?.length || 0,
      had_remove_cleaned: !!result._blRemoveCleaned,
      had_consolidated: !!result._blConsolidated,
      output: result
    });

    originalPush(result);
  }

  function scheduleFlush() {
    if (flushTimer) return;
    flushTimer = setTimeout(flush, BUFFER_WINDOW_MS);
  }

  function wrappedPush(...args) {
    const passthrough = [];

    args.forEach((arg) => {
      if (isAddToCart(arg)) {
        buffer.push(arg);
        logVerbose(`buffered dl_add_to_cart (${buffer.length} in queue)`, {
          variant_id: arg?.ecommerce?.add?.products?.[0]?.variant_id,
          name: arg?.ecommerce?.add?.products?.[0]?.name
        });
      } else {
        passthrough.push(arg);
      }
    });

    // Non-AddToCart events: forward immediately, preserving order.
    passthrough.forEach((arg) => originalPush(arg));

    if (buffer.length > 0) scheduleFlush();

    // ElevarDataLayer.push returns the new length; we approximate it.
    return window.ElevarDataLayer.length;
  }

  function attach(attempts) {
    const tries = attempts || 0;

    if (!Array.isArray(window.ElevarDataLayer)) {
      if (tries >= ATTACH_MAX_TRIES) {
        logVerbose('attach gave up — window.ElevarDataLayer never appeared');
        return;
      }
      setTimeout(() => attach(tries + 1), ATTACH_RETRY_MS);
      return;
    }

    if (window.ElevarDataLayer.__blAtcBridgeAttached) return;

    originalPush = window.ElevarDataLayer.push.bind(window.ElevarDataLayer);
    window.ElevarDataLayer.push = wrappedPush;
    window.ElevarDataLayer.__blAtcBridgeAttached = true;

    if (VERBOSE) {
      // eslint-disable-next-line no-console
      console.log(
        '%c[BL-atc-bridge] ATTACHED',
        'color:#fff;background:#16a34a;padding:2px 8px;border-radius:3px;font-weight:bold',
        { window_ms: BUFFER_WINDOW_MS, existing_length: window.ElevarDataLayer.length }
      );
    }
  }

  attach();
})();
